import React, { useState, useEffect, useRef } from "react";
import useSWR from "swr";
import { api } from "@/lib/api";
import { Spinner, HelpIcon } from "@/components/Atoms";
import { Terminal, Plus, Trash2, Save, RotateCcw, ShieldAlert, CheckCircle2 } from "lucide-react";

const fetcher = (u) => api.get(u).then((r) => r.data);

// allow object {verb: true | [flags]} -> editable rows
const toRows = (allow) =>
    Object.entries(allow || {}).map(([verb, rule]) => ({
        verb,
        any: rule === true,
        flags: Array.isArray(rule) ? rule.join(" ") : "",
    }));

const rowsToAllow = (rows) => {
    const out = {};
    for (const r of rows) {
        const v = (r.verb || "").trim();
        if (!v) continue;
        out[v] = r.any
            ? true
            : (r.flags || "").split(/[\s,]+/).map((x) => x.trim()).filter(Boolean);
    }
    return out;
};

export default function OccPolicyPanel() {
    const { data, error, mutate, isLoading } = useSWR("/api/admin/occ-policy", fetcher);
    const [rows, setRows] = useState([]);
    const [warn, setWarn] = useState([]);
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [savedAt, setSavedAt] = useState(null);
    const [newVerb, setNewVerb] = useState("");
    const [newWarn, setNewWarn] = useState("");
    const loadedRef = useRef(false);

    // sync from server when data arrives and there are no unsaved edits
    useEffect(() => {
        if (data && !dirty) {
            setRows(toRows(data.allow));
            setWarn([...(data.warn || [])]);
            loadedRef.current = true;
        }
    }, [data, dirty]);

    const markDirty = () => setDirty(true);

    const updateRow = (i, patch) => {
        setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
        markDirty();
    };
    const removeRow = (i) => { setRows((rs) => rs.filter((_, idx) => idx !== i)); markDirty(); };
    const addVerb = () => {
        const v = newVerb.trim();
        if (!v) return;
        if (rows.some((r) => r.verb === v)) { alert(`Verb "${v}" is already in the allow list.`); return; }
        setRows((rs) => [...rs, { verb: v, any: true, flags: "" }]);
        setNewVerb(""); markDirty();
    };
    const addWarn = () => {
        const v = newWarn.trim();
        if (!v || warn.includes(v)) { setNewWarn(""); return; }
        setWarn((w) => [...w, v]); setNewWarn(""); markDirty();
    };
    const removeWarn = (v) => { setWarn((w) => w.filter((x) => x !== v)); markDirty(); };

    const reset = () => {
        if (data) { setRows(toRows(data.allow)); setWarn([...(data.warn || [])]); }
        setDirty(false);
    };

    const save = async () => {
        setSaving(true);
        try {
            const body = { allow: rowsToAllow(rows), warn };
            const r = await api.put("/api/admin/occ-policy", body);
            setDirty(false);
            setSavedAt(r?.data?.updated_at || new Date().toISOString());
            await mutate();
        } catch (e) {
            alert(e?.response?.data?.detail || String(e));
        } finally {
            setSaving(false);
        }
    };

    if (isLoading && !data) return <Spinner label="Loading OCC policy…" />;
    if (error) return <div className="text-red-400 text-sm" data-testid="occ-policy-error">Error loading the OCC policy.</div>;

    return (
        <div data-testid="occ-policy-panel" className="space-y-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <h2 className="text-lg font-mono uppercase tracking-widest text-zinc-100 flex items-center gap-2">
                        <Terminal size={16} className="text-cm-brand" /> OCC Command Policy
                    </h2>
                    <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
                        DB-driven allow list of the OCC verbs resellers may run themselves,
                        plus warn/block verbs (superadmin audit flag). Changes apply fleet-wide
                        (shortly cached). <HelpIcon text="Fully DB-driven — defaults are seeded once from code, then editable here." />
                    </p>
                    <p className="text-xs text-zinc-500 mt-1 font-mono" data-testid="occ-policy-meta">
                        Last: {data?.updated_at ? new Date(data.updated_at).toLocaleString() : "—"}
                        {data?.updated_by ? ` · ${data.updated_by === "seed" ? "seed (default)" : data.updated_by}` : ""}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {savedAt && !dirty && (
                        <span className="text-xs text-emerald-400 flex items-center gap-1" data-testid="occ-policy-saved">
                            <CheckCircle2 size={14} /> saved
                        </span>
                    )}
                    <button
                        data-testid="occ-policy-reset-btn"
                        onClick={reset}
                        disabled={!dirty || saving}
                        className="px-3 py-2 text-xs font-mono uppercase tracking-widest bg-zinc-900 text-zinc-300 hover:text-white disabled:opacity-40 flex items-center gap-2"
                    >
                        <RotateCcw size={12} /> Reset
                    </button>
                    <button
                        data-testid="occ-policy-save-btn"
                        onClick={save}
                        disabled={!dirty || saving}
                        className="px-4 py-2 text-xs font-mono uppercase tracking-widest bg-[#CCFF00] text-[#05070A] hover:bg-[#9FCC00] disabled:opacity-40 flex items-center gap-2"
                    >
                        <Save size={12} /> {saving ? "Saving…" : "Save"}
                    </button>
                </div>
            </div>

            {/* Allow list */}
            <div className="border border-zinc-800 bg-zinc-950">
                <div className="px-4 py-2 border-b border-zinc-800 flex items-center justify-between">
                    <span className="text-xs font-mono uppercase tracking-widest text-zinc-300">
                        Allow verbs ({rows.length})
                    </span>
                    <span className="text-xs text-zinc-500">Resellers may run these OCC verbs</span>
                </div>
                <div className="divide-y divide-zinc-900">
                    {rows.map((r, i) => (
                        <div key={i} className="flex flex-wrap items-center gap-3 px-4 py-2" data-testid={`occ-allow-row-${r.verb}`}>
                            <code className="text-sm text-[#5b8cff] font-mono min-w-[220px]">{r.verb}</code>
                            <label className="flex items-center gap-1.5 text-xs text-zinc-400">
                                <input
                                    type="checkbox"
                                    data-testid={`occ-allow-any-${r.verb}`}
                                    checked={r.any}
                                    onChange={(e) => updateRow(i, { any: e.target.checked })}
                                    className="accent-[#CCFF00]"
                                />
                                All subcommands
                            </label>
                            {!r.any && (
                                <span className="text-[10px] text-zinc-600 font-mono"
                                      data-testid={`occ-allow-hint-${r.verb}`}>
                                    Einträge mit "--" = erlaubte Flags · ohne "--" = erlaubte App-Namen (1. Argument, Flags dann frei)
                                </span>
                            )}
                            {!r.any && (
                                <input
                                    data-testid={`occ-allow-flags-${r.verb}`}
                                    value={r.flags}
                                    onChange={(e) => updateRow(i, { flags: e.target.value })}
                                    placeholder="--flag1 --flag2 | oder App-Namen: integration_google"
                                    className="flex-1 min-w-[180px] bg-zinc-900 border border-zinc-800 px-2 py-1 text-xs font-mono text-zinc-200 focus:border-[#CCFF00] outline-none"
                                />
                            )}
                            <button
                                data-testid={`occ-allow-remove-${r.verb}`}
                                onClick={() => removeRow(i)}
                                className="ml-auto text-zinc-500 hover:text-red-400"
                                title="Remove"
                            >
                                <Trash2 size={14} />
                            </button>
                        </div>
                    ))}
                    {rows.length === 0 && (
                        <div className="px-4 py-3 text-xs text-zinc-500">No allow verbs — reseller OCC is fully locked.</div>
                    )}
                </div>
                <div className="flex items-center gap-2 px-4 py-3 border-t border-zinc-800 bg-zinc-950/60">
                    <input
                        data-testid="occ-allow-new-input"
                        value={newVerb}
                        onChange={(e) => setNewVerb(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && addVerb()}
                        placeholder="e.g. files:cleanup"
                        className="flex-1 bg-zinc-900 border border-zinc-800 px-2 py-1.5 text-sm font-mono text-zinc-200 focus:border-[#CCFF00] outline-none"
                    />
                    <button
                        data-testid="occ-allow-add-btn"
                        onClick={addVerb}
                        className="px-3 py-1.5 text-xs font-mono uppercase tracking-widest bg-zinc-800 text-zinc-200 hover:bg-zinc-700 flex items-center gap-2"
                    >
                        <Plus size={12} /> Add verb
                    </button>
                </div>
            </div>

            {/* Warn / Block list */}
            <div className="border border-zinc-800 bg-zinc-950">
                <div className="px-4 py-2 border-b border-zinc-800 flex items-center gap-2">
                    <ShieldAlert size={14} className="text-amber-400" />
                    <span className="text-xs font-mono uppercase tracking-widest text-zinc-300">
                        Warn / block verbs ({warn.length})
                    </span>
                    <span className="text-xs text-zinc-500 ml-auto">Superadmin execution is flagged in the audit log</span>
                </div>
                <div className="flex flex-wrap gap-2 px-4 py-3">
                    {warn.map((v) => (
                        <span key={v} data-testid={`occ-warn-chip-${v}`} className="inline-flex items-center gap-1.5 bg-amber-950/40 border border-amber-900/60 text-amber-300 text-xs font-mono px-2 py-1">
                            {v}
                            <button data-testid={`occ-warn-remove-${v}`} onClick={() => removeWarn(v)} className="hover:text-red-400" title="Remove">
                                <Trash2 size={12} />
                            </button>
                        </span>
                    ))}
                    {warn.length === 0 && <span className="text-xs text-zinc-500">No warn verbs.</span>}
                </div>
                <div className="flex items-center gap-2 px-4 py-3 border-t border-zinc-800 bg-zinc-950/60">
                    <input
                        data-testid="occ-warn-new-input"
                        value={newWarn}
                        onChange={(e) => setNewWarn(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && addWarn()}
                        placeholder="e.g. user:delete"
                        className="flex-1 bg-zinc-900 border border-zinc-800 px-2 py-1.5 text-sm font-mono text-zinc-200 focus:border-amber-500 outline-none"
                    />
                    <button
                        data-testid="occ-warn-add-btn"
                        onClick={addWarn}
                        className="px-3 py-1.5 text-xs font-mono uppercase tracking-widest bg-zinc-800 text-zinc-200 hover:bg-zinc-700 flex items-center gap-2"
                    >
                        <Plus size={12} /> Add verb
                    </button>
                </div>
            </div>

            {dirty && (
                <p className="text-xs text-amber-400" data-testid="occ-policy-dirty">
                    Unsaved changes — apply with “Save”.
                </p>
            )}
        </div>
    );
}
